/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Button, Collapse, ICollapseProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IQuickOrderGridDrawerProps } from './quick-order-grid-drawer-props';

/**
 * Interface for drawer state.
 */
export interface IQuickOrderGridDrawerState {
    isOpen: boolean;
}

/**
 * Placement of glyph.
 */
export type GlyphPlacement = 'start' | 'end';

/**
 * Quick Order Grid Drawer - This is a full width uncontrolled collapse where the button glyph changes
 * when the drawer is open and closed.
 */
export class QuickOrderGridDrawer extends React.Component<IQuickOrderGridDrawerProps, IQuickOrderGridDrawerState> {
    private readonly _drawerOpenGlyph: string;

    private readonly _drawerCloseGlyph: string;

    private readonly _drawerGlyphClass: string;

    private _isManuallyToggled: boolean;

    public constructor(props: IQuickOrderGridDrawerProps) {
        super(props);
        this._isManuallyToggled = false;
        this.state = { isOpen: props.collapseProps?.isOpen ?? false };
        this._drawerGlyphClass = classnames('drawer__glyph', this.props.toggleGlyphClassName ? this.props.toggleGlyphClassName : '');
        const closeGlyph = this.props.closeGlyph ? this.props.closeGlyph : '';
        if (this.props.isAnimateGlyph) {
            this._drawerOpenGlyph = classnames(this._drawerGlyphClass, this.props.openGlyph ? this.props.openGlyph : closeGlyph);
            this._drawerCloseGlyph = this._drawerOpenGlyph;
        } else {
            this._drawerOpenGlyph = classnames(this._drawerGlyphClass, this.props.openGlyph ? this.props.openGlyph : '');
            this._drawerCloseGlyph = classnames(this._drawerGlyphClass, closeGlyph);
        }
    }

    /**
     * Method to call on click of drawer button.
     * @param event - Product DeliveryOption List.
     */
    public toggle = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void => {
        event.preventDefault();
        this._isManuallyToggled = true;
        if (this.props.onToggle) {
            this.setState(previousState => ({ isOpen: !previousState.isOpen }));
            this.props.onToggle(this.state);
        } else {
            this.setState(previousState => ({ isOpen: !previousState.isOpen }));
        }
    };

    public shouldComponentUpdate(nextProps: IQuickOrderGridDrawerProps, nextState: IQuickOrderGridDrawerState): boolean {
        if (this.state === nextState && this.props === nextProps) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const collapseProps: ICollapseProps = { ...this.props.collapseProps };

        // If the drawer is manually toggled by pressing the button, then we use the internal state
        if (collapseProps.isOpen === undefined || this._isManuallyToggled) {
            collapseProps.isOpen = this.state.isOpen;
        }
        collapseProps.children = this.props.children;
        const drawerClass = classnames('drawer', this.props.className);
        const drawerButtonClassName = `${this.props.className ? this.props.className : ''}__button`;
        this._isManuallyToggled = false;
        return (
            <div className={drawerClass}>
                <Button
                    {...this.props.toggleButtonProps}
                    onClick={this.toggle}
                    aria-expanded={collapseProps.isOpen}
                    block
                    disabled={this.props.isDisabled}
                    className={drawerButtonClassName}
                    color='secondary'
                >
                    {this._generateDrawerButtonInnerMarkup(collapseProps.isOpen, drawerClass)}
                </Button>
                <Collapse {...collapseProps} />
            </div>
        );
    }

    private _generateDrawerButtonInnerMarkup(isCollapseState: boolean, drawerClass: string): JSX.Element {
        const drawerButtonTextClass = classnames(
            'drawer__buttontext',
            this.props.toggleButtonClassName ? this.props.toggleButtonClassName : ''
        );
        const headingDrawerClass = `${drawerClass}-heading`;
        const descriptionDrawerClass = `${drawerClass}-description`;
        return (
            <>
                <h2 className={classnames(drawerButtonTextClass, headingDrawerClass)}>{this.props.moduleHeadingText}</h2>
                <span className={classnames(drawerButtonTextClass, descriptionDrawerClass)}>{this.props.moduleDescriptionText}</span>
                {this._generateGlyphMarkup('drawer__glyph__end', isCollapseState)}
            </>
        );
    }

    private _generateGlyphMarkup(location: string, isCollapseState: boolean): JSX.Element {
        let glyphClass = isCollapseState ? this._drawerOpenGlyph : this._drawerCloseGlyph;
        glyphClass = `${glyphClass} ${location}`;
        return <span className={glyphClass} />;
    }
}
